import { useState, useEffect, FormEvent } from 'react'
import { extractErrorMessage } from '@/utils/errorHandler'
import { engineerApi, EngineerScopeInfo, ScopesConfigResponse } from '@/services/api/superadmin'
import { EngineerScopeAccordion } from '@/components/superadmin/EngineerScopeAccordion'
import { Button, Input, Modal, Select } from '@/components/common'

interface CreateForm {
  email: string
  first_name: string
  last_name: string
  scopes: string[]
  token_validity_hours: number
}

const emptyForm: CreateForm = {
  email: '',
  first_name: '',
  last_name: '',
  scopes: [],
  token_validity_hours: 168,
}

const TOKEN_VALIDITY_LABELS = [
  { label: '24 Stunden', value: 24 },
  { label: '7 Tage', value: 168 },
  { label: '30 Tage', value: 720 },
  { label: '90 Tage', value: 2160 },
]

const STATUS_FILTERS = [
  { key: '', label: 'Alle' },
  { key: 'active', label: 'Aktiv' },
  { key: 'expired', label: 'Abgelaufen' },
  { key: 'inactive', label: 'Inaktiv' },
]

function kindLabel(kind: EngineerScopeInfo['kind'], stammrolle?: string | null) {
  if (kind === 'intern') return stammrolle ? `Intern (${stammrolle})` : 'Intern'
  if (kind === 'partner') return 'Partner'
  return 'Extern'
}

export function EngineerManagementPage() {
  const [engineers, setEngineers] = useState<EngineerScopeInfo[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<CreateForm>(emptyForm)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selectedEngineer, setSelectedEngineer] = useState<EngineerScopeInfo | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<EngineerScopeInfo | null>(null)
  const [scopesConfig, setScopesConfig] = useState<ScopesConfigResponse | null>(null)
  const [editScopes, setEditScopes] = useState<string[]>([])
  const [extendHours, setExtendHours] = useState(168)

  const fetchEngineers = async () => {
    setLoading(true)
    try {
      const res = await engineerApi.list({ status_filter: statusFilter || undefined, search: search || undefined, skip: 0, limit: 200 })
      setEngineers(res.engineers)
      setTotal(res.total)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Engineers konnten nicht geladen werden' }))
    } finally {
      setLoading(false)
    }
  }

  const fetchScopesConfig = async () => {
    try {
      setScopesConfig(await engineerApi.getScopesConfig())
    } catch (err) {
      console.error('Failed to load scopes config:', err)
    }
  }

  useEffect(() => { fetchEngineers() }, [statusFilter])
  useEffect(() => { fetchScopesConfig() }, [])
  useEffect(() => {
    if (!message) return
    const t = setTimeout(() => setMessage(null), 5000)
    return () => clearTimeout(t)
  }, [message])
  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(null), 5000)
    return () => clearTimeout(t)
  }, [error])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (form.scopes.length === 0) {
      setError('Mindestens ein Scope muss ausgewählt sein.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await engineerApi.create({
        email: form.email,
        first_name: form.first_name || undefined,
        last_name: form.last_name || undefined,
        scopes: form.scopes,
        token_validity_hours: form.token_validity_hours,
      })
      setShowCreate(false)
      setForm(emptyForm)
      setMessage('Engineer gespeichert.')
      await fetchEngineers()
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Erstellen' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!confirmDelete) return
    setSubmitting(true)
    try {
      await engineerApi.delete(confirmDelete.id)
      setMessage(`"${confirmDelete.email}" gelöscht.`)
      setConfirmDelete(null)
      if (selectedEngineer?.id === confirmDelete.id) setSelectedEngineer(null)
      await fetchEngineers()
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Löschen' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleActive = async (eng: EngineerScopeInfo) => {
    setSubmitting(true)
    try {
      if (eng.is_active) {
        await engineerApi.deactivate(eng.id)
        setMessage(`"${eng.email}" deaktiviert.`)
      } else if (eng.is_expired) {
        await engineerApi.extend(eng.id, extendHours)
        setMessage(`"${eng.email}" verlängert.`)
      } else {
        await engineerApi.activate(eng.id)
        setMessage(`"${eng.email}" aktiviert.`)
      }
      await fetchEngineers()
      if (selectedEngineer?.id === eng.id) {
        setSelectedEngineer(await engineerApi.get(eng.id))
      }
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Ändern des Status' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRegenerateToken = async (id: string) => {
    setSubmitting(true)
    try {
      const res = await engineerApi.regenerateToken(id)
      setMessage('Neuer Einladungs-Token generiert und gesendet.')
      await fetchEngineers()
      setSelectedEngineer(res)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Generieren' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleExtend = async () => {
    if (!selectedEngineer) return
    setSubmitting(true)
    try {
      const res = await engineerApi.extend(selectedEngineer.id, extendHours)
      setMessage('Mitgliedschaft verlängert.')
      setSelectedEngineer(res)
      await fetchEngineers()
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Verlängern' }))
    } finally {
      setSubmitting(false)
    }
  }

  const openEdit = (eng: EngineerScopeInfo) => {
    setSelectedEngineer(eng)
    setEditScopes(eng.scopes || [])
  }

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault()
    if (!selectedEngineer) return
    if (editScopes.length === 0) {
      setError('Mindestens ein Scope muss ausgewählt sein.')
      return
    }
    setSubmitting(true)
    try {
      const res = await engineerApi.update(selectedEngineer.id, { scopes: editScopes })
      setMessage('Engineer aktualisiert.')
      setSelectedEngineer(res)
      await fetchEngineers()
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Aktualisieren' }))
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusBadge = (eng: EngineerScopeInfo) => {
    if (!eng.is_active) return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Inaktiv</span>
    if (eng.is_expired) return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">Abgelaufen</span>
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Aktiv</span>
  }

  if (loading && !scopesConfig) return <div className="flex items-center justify-center min-h-[60vh]"><p className="text-gray-500">Laden...</p></div>

  const accordions = scopesConfig?.accordions ?? []

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Engineer-Verwaltung</h1>
          <p className="text-sm text-gray-500 mt-1">Schul-IT intern oder extern, zeitlich begrenzt, mit Scopes</p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setForm(emptyForm); setError(null) }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Engineer einladen
        </button>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded flex items-center justify-between">
          <span>{message}</span>
          <button onClick={() => setMessage(null)} className="text-green-500 hover:text-green-700">x</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">x</button>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 text-sm rounded-md border ${statusFilter === f.key ? 'bg-gray-100 border-gray-300 font-medium' : 'bg-white border-gray-200'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchEngineers() }}
          placeholder="Name oder E-Mail suchen..."
          className="rounded-md border-gray-300 shadow-sm px-3 py-2 border w-full max-w-md"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Alle Engineers ({total})</h3>
          {engineers.length === 0 ? (
            <p className="text-gray-400 text-sm">Keine Engineers gefunden.</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {engineers.map((eng) => (
                <button
                  key={eng.id}
                  onClick={() => openEdit(eng)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-50 border transition ${selectedEngineer?.id === eng.id ? 'bg-blue-50 border-blue-200' : 'border-transparent'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{eng.first_name} {eng.last_name}</span>
                    {getStatusBadge(eng)}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{eng.email}</p>
                  <p className="text-[11px] text-gray-400">{kindLabel(eng.kind, eng.stammrolle)}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          {selectedEngineer ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {selectedEngineer.first_name} {selectedEngineer.last_name}
                </h3>
                {getStatusBadge(selectedEngineer)}
              </div>
              <p className="text-sm text-gray-600 mb-1">{selectedEngineer.email}</p>
              <p className="text-sm text-gray-500 mb-1">
                {kindLabel(selectedEngineer.kind, selectedEngineer.stammrolle)}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {selectedEngineer.is_expired
                  ? 'Abgelaufen'
                  : selectedEngineer.expires_at
                    ? `Gültig bis: ${new Date(selectedEngineer.expires_at).toLocaleDateString('de-CH')}`
                    : 'Kein Ablaufdatum gesetzt'}
              </p>

              <form onSubmit={handleSaveEdit}>
                {accordions.length > 0 && (
                  <EngineerScopeAccordion accordions={accordions} scopes={editScopes} onChange={setEditScopes} />
                )}
                <div className="flex flex-wrap gap-2 mt-6">
                  <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                    {submitting ? 'Speichern...' : 'Scopes speichern'}
                  </button>
                  {selectedEngineer.kind === 'extern' && !selectedEngineer.token_used && (
                    <button type="button" onClick={() => handleRegenerateToken(selectedEngineer.id)} disabled={submitting} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
                      Neuen Token senden
                    </button>
                  )}
                  <button type="button" onClick={() => handleToggleActive(selectedEngineer)} disabled={submitting} className={`px-4 py-2 border rounded-md disabled:opacity-50 ${selectedEngineer.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
                    {selectedEngineer.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(selectedEngineer)} disabled={submitting} className="px-4 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50 ml-auto">
                    Löschen
                  </button>
                </div>
              </form>

              <div className="mt-6 pt-4 border-t flex items-end gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Verlängern</label>
                  <select value={extendHours} onChange={(e) => setExtendHours(Number(e.target.value))} className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm">
                    {TOKEN_VALIDITY_LABELS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <button type="button" onClick={handleExtend} disabled={submitting} className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm disabled:opacity-50">
                  Ablauf setzen
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-12">Wähle einen Engineer aus der Liste.</p>
          )}
        </div>
      </div>

      {showCreate && (
        <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Engineer einladen" size="lg">
          <p className="text-sm text-gray-500 mb-4">Bestehende Schulmails werden intern ohne Token angehängt. Neue Mails erhalten eine Einladung.</p>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="E-Mail *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  type="email"
                  required
                />
                <Select
                  label="Gültigkeit"
                  value={String(form.token_validity_hours)}
                  onChange={(e) => setForm({ ...form, token_validity_hours: Number(e.target.value) })}
                  options={TOKEN_VALIDITY_LABELS.map((o) => ({ value: String(o.value), label: o.label }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Vorname (nur neu)"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  type="text"
                />
                <Input
                  label="Nachname (nur neu)"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  type="text"
                />
              </div>
              {accordions.length > 0 ? (
                <EngineerScopeAccordion accordions={accordions} scopes={form.scopes} onChange={(v) => setForm({ ...form, scopes: v })} />
              ) : (
                <p className="text-sm text-gray-400">Scopes laden...</p>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Abbrechen</Button>
              <Button type="submit" disabled={submitting} isLoading={submitting}>
                Speichern
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-2">Mitgliedschaft löschen</h2>
            <p className="text-sm text-gray-600 mb-4">
              Mitgliedschaft von <strong>{confirmDelete.email}</strong> an dieser Schule entfernen?
            </p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
              <button onClick={handleDelete} disabled={submitting} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
